import { useLocation } from 'react-router-dom'
import ClientShell from '@/components/layout/ClientShell'
import JsonLd from '@/components/seo/JsonLd'
import { useDocumentMeta } from '@/lib/useDocumentMeta'
import { SITE_URL, breadcrumbSchema, metaForPath, urlFor, type Locale } from '@/lib/seo'
import { toLocale, useI18n } from '@/lib/i18n'
import { SECTION_PATHS, sectionForPath } from '@/lib/sections'
import NotFoundPage from '@/pages/NotFoundPage'

/** Paths the single-page shell serves; anything else is a 404. */
export const SHELL_PATHS = Object.values(SECTION_PATHS)

function webPageSchema(path: string, locale: Locale) {
  const meta = metaForPath(path, locale)
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${urlFor(path, locale)}#webpage`,
    name: meta.title,
    url: urlFor(path, locale),
    description: meta.description,
    inLanguage: locale,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#organization` },
  }
}

function servicesSchema() {
  const provider = { '@id': `${SITE_URL}/#organization` }
  const areaServed = [
    { '@type': 'Place', name: 'Vienna' },
    { '@type': 'Country', name: 'Austria' },
  ]
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Web application development',
      provider,
      areaServed,
      availableLanguage: ['en', 'de'],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Mobile app development',
      provider,
      areaServed,
      availableLanguage: ['en', 'de'],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Website design and implementation',
      provider,
      areaServed,
      availableLanguage: ['en', 'de'],
    },
  ]
}

export default function ShellPage() {
  const { pathname } = useLocation()
  if (!SHELL_PATHS.includes(pathname)) return <NotFoundPage />
  return <Shell pathname={pathname} />
}

function Shell({ pathname }: { pathname: string }) {
  const locale = toLocale(useI18n().lang)
  const section = sectionForPath(pathname)
  const meta = metaForPath(pathname, locale)
  const crumbs = breadcrumbSchema(pathname, locale)

  useDocumentMeta({ ...meta, path: pathname, locale })

  return (
    <>
      <ClientShell initial={section} />

      <JsonLd data={webPageSchema(pathname, locale)} />
      {/* A one-item trail on the home page adds nothing. */}
      {crumbs && <JsonLd data={crumbs} />}
      {(pathname === '/' || pathname === SECTION_PATHS.about) && <JsonLd data={servicesSchema()} />}
    </>
  )
}
